import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type ChapterQuestions, type ReadingProgress } from "../api";
import { useAuth } from "../auth";

interface Props {
  bookId: number;
  chapters: ChapterQuestions[];
}

export function DiscussionQuestionsSection({ bookId, chapters }: Props) {
  const { user } = useAuth();

  const progress = useQuery({
    queryKey: ["progress", bookId],
    queryFn: () => api.get<{ progress: ReadingProgress[] }>(`/progress/${bookId}`),
  });

  const myProgress = useMemo(() => {
    return progress.data?.progress.find((p) => p.userId === user?.id)?.currentChapter ?? null;
  }, [progress.data, user]);

  const maxChapter = chapters.reduce((m, c) => Math.max(m, c.number), 0);
  const defaultUpTo = myProgress != null ? Math.min(Math.max(myProgress, 1), maxChapter) : maxChapter;
  const [upTo, setUpTo] = useState<number>(defaultUpTo);

  const visible = chapters.filter((c) => c.number <= upTo);

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col md:flex-row md:items-center gap-4 p-5 rounded-xl bg-cream-50/70 border border-cream-200">
        <div className="flex-1">
          <div className="text-[0.7rem] uppercase tracking-[0.18em] text-gold-dark mb-1.5">
            Spoiler control
          </div>
          <p className="text-sm text-plum-soft">
            Show questions for chapters up through:
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={1}
            max={maxChapter}
            value={upTo}
            onChange={(e) => setUpTo(Number(e.target.value))}
            className="w-48 accent-rose"
          />
          <span className="font-serif text-2xl text-plum w-10 text-right">{upTo}</span>
        </div>
      </div>

      <div className="flex flex-col gap-8">
        {visible.map((ch) => (
          <article key={ch.number} className="flex gap-5">
            <div className="shrink-0 w-12 text-center">
              <div className="font-serif italic text-rose text-3xl leading-none">{ch.number}</div>
              <div className="text-[0.6rem] uppercase tracking-[0.18em] text-plum-mute mt-1">
                Chapter
              </div>
            </div>
            <div className="flex-1 min-w-0">
              {ch.title ? (
                <h4 className="font-serif text-lg text-plum mb-3">{ch.title}</h4>
              ) : null}
              <ul className="flex flex-col gap-3">
                {ch.questions.map((q, i) => (
                  <li
                    key={i}
                    className="flex gap-3 leading-relaxed text-plum"
                  >
                    <span className="text-gold mt-0.5">◆</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
